/**
 * src/lib/sync.js — đồng bộ Bản chuẩn bị lên tài khoản.
 *
 * Một người dùng = một document `workspaces/{uid}`, chứa đúng cục JSON đang
 * nằm trong localStorage. Không phát sinh loại dữ liệu mới, không tách bảng,
 * không chuẩn hoá — vì thứ duy nhất cần làm là cho cùng một dữ liệu xuất hiện
 * trên hai thiết bị.
 *
 * Giới hạn: một document Firestore tối đa 1 MB. Bản chuẩn bị bình thường chỉ
 * vài chục KB, nhưng logo dán vào hồ sơ có thể đẩy lên nhanh, nên phải chặn
 * trước khi gửi thay vì để Firestore trả lỗi khó hiểu.
 */

import { layDb } from './firebase.js';
import { validWorkspace } from './validateWorkspace.js';
import { gopWorkspace } from '@shared/gopWorkspace.js';

/** Chừa chỗ cho phần Firestore tự thêm vào; 1 MB là trần cứng. */
export const TRAN_BYTE = 800000;

export function coBytes(x) {
  return new TextEncoder().encode(JSON.stringify(x)).length;
}

async function tepCua(uid) {
  const db = await layDb();
  if (!db) throw new Error('Website chưa cấu hình Firestore.');
  const { doc } = await import('firebase/firestore/lite');
  return { db, ref: doc(db, 'workspaces', uid) };
}

/**
 * Đọc bản trên tài khoản. Trả về null khi chưa có, hoặc khi document hỏng —
 * hỏng thì coi như chưa có, để bản trên thiết bị không bị bản rác ghi đè.
 */
export async function taiVe(uid) {
  const { ref } = await tepCua(uid);
  const { getDoc } = await import('firebase/firestore/lite');
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  const d = snap.data();
  const ws = d?.workspace;
  if (!validWorkspace(ws)) {
    console.warn('Bản trên tài khoản không đúng định dạng, bỏ qua.');
    return null;
  }
  return ws;
}

/** Ghi đè bản trên tài khoản bằng dữ liệu đã gộp. */
export async function dayLen(uid, ws, email) {
  if (!validWorkspace(ws)) throw new Error('Dữ liệu không đúng định dạng, chưa đồng bộ.');
  const co = coBytes(ws);
  if (co > TRAN_BYTE)
    throw new Error(
      `Bản chuẩn bị đang nặng ${Math.round(co / 1024)} KB, vượt mức đồng bộ được `
      + `(${Math.round(TRAN_BYTE / 1024)} KB). Hãy bỏ bớt kịch bản cũ hoặc dùng logo nhẹ hơn.`,
    );
  const { ref } = await tepCua(uid);
  const { setDoc } = await import('firebase/firestore/lite');
  await setDoc(ref, {
    workspace: ws,
    email: email || null,
    capNhat: new Date().toISOString(),
  });
  return ws;
}

/**
 * Đồng bộ hai chiều trong MỘT transaction.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * VÌ SAO PHẢI LÀ TRANSACTION: bản cũ đọc rồi mới ghi, hai thao tác tách rời.
 * Hai thiết bị bấm đồng bộ gần như cùng lúc thì cả hai cùng đọc được bản cũ,
 * cùng gộp, rồi lần ghi sau đè lên lần ghi trước — thiết bị thua mất sạch
 * phần vừa nhập mà không có dấu hiệu gì. Transaction bắt Firestore kiểm tra
 * document có bị đổi giữa chừng không; nếu có thì tự chạy lại phép gộp với
 * dữ liệu mới nhất.
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Trả về bản đã gộp để bên gọi ghi xuống localStorage.
 */
export async function dongBo(uid, banThietBi, email) {
  if (!validWorkspace(banThietBi))
    throw new Error('Dữ liệu trên máy không đúng định dạng, chưa đồng bộ.');
  const { db, ref } = await tepCua(uid);
  const { runTransaction } = await import('firebase/firestore/lite');

  return runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    let banTaiKhoan = null;
    if (snap.exists()) {
      const ws = snap.data()?.workspace;
      // Document hỏng thì coi như chưa có, để bản trên máy không bị rác ghi đè.
      if (validWorkspace(ws)) banTaiKhoan = ws;
      else console.warn('Bản trên tài khoản không đúng định dạng, bỏ qua.');
    }
    const gop = banTaiKhoan ? gopWorkspace(banThietBi, banTaiKhoan) : banThietBi;

    const co = coBytes(gop);
    if (co > TRAN_BYTE)
      throw new Error(
        `Bản chuẩn bị đang nặng ${Math.round(co / 1024)} KB, vượt mức đồng bộ được `
        + `(${Math.round(TRAN_BYTE / 1024)} KB). Hãy bỏ bớt kịch bản cũ hoặc dùng logo nhẹ hơn.`,
      );

    tx.set(ref, { workspace: gop, email: email || null, capNhat: new Date().toISOString() });
    return gop;
  });
}

/**
 * Xoá dữ liệu trên tài khoản. Gọi TRƯỚC khi xoá tài khoản Auth: mất phiên đăng
 * nhập rồi thì rules không cho xoá nữa và dữ liệu nằm lại vĩnh viễn.
 */
export async function xoaTrenTaiKhoan(uid) {
  const { ref } = await tepCua(uid);
  const { deleteDoc } = await import('firebase/firestore/lite');
  await deleteDoc(ref);
}
